import { ChartSection, ChartHeader } from '@instamenu/ui-system-next';
import { ViewMoreTable } from '@/components/core/tables';
import { formatCurrency } from '@instamenu/formatters';
import { useSalesSummaryContext } from '../../SalesSummaryContext';
import { formatTimespan, formatTimeRange } from '@instamenu/formatters';
import { useMediaQuery } from 'react-responsive';
import { useLocale } from '@instamenu/context-i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ItemStats } from '@instamenu/data-access-stats';
import { columnHelpers } from '@/components/core/tables/column-helpers';
import { sumBy } from 'lodash-es';

const columnHelper = createColumnHelper<ItemStats>();

export default function TopItems() {
  const { i18n } = useLocale();
  const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });
  const { statsQuery, timespan, times } = useSalesSummaryContext();
  const [{ data, error, fetching }] = statsQuery;

  const columns = [
    columnHelper.accessor('quantity', {
      header: isDesktop
        ? i18n.t('salesSummary.topItems.quantitySold')
        : i18n.t('salesSummary.topItems.qty'),
      size: 100,
      footer: () => sumBy(data?.stats.topItems, (x) => x.quantity),
    }),
    columnHelper.accessor('itemGroupName', {
      header: i18n.t('salesSummary.topItems.itemGroup'),
      size: 200,
      sortingFn: (rowA, rowB, id) => {
        // from https://github.com/TanStack/table/discussions/4920#discussioncomment-7949994
        const direction =
          rowA._getAllCellsByColumnId()[id].column.getIsSorted() === 'desc' ? 1 : -1;

        // Sort by amount if the names are the same, always put highest amount first
        return rowA.original.itemGroupName === rowB.original.itemGroupName
          ? (rowA.original.amount - rowB.original.amount) * direction
          : !rowA.original.itemGroupName
            ? 1
            : !rowB.original.itemGroupName
              ? -1
              : (rowA.original.itemGroupName?.localeCompare(rowB.original.itemGroupName) ?? 1);
      },
    }),
    columnHelper.accessor('name', {
      header: i18n.t('salesSummary.topItems.itemName'),
      size: 500,
    }),
    columnHelper.accessor('discount', {
      ...columnHelpers.currency,
      header: i18n.t('salesSummary.topItems.discount'),
      size: 100,
      footer: () => formatCurrency(sumBy(data?.stats.topItems, (x) => x.discount)),
    }),
    columnHelper.accessor('amount', {
      ...columnHelpers.currency,
      header: i18n.t('salesSummary.topItems.sales'),
      size: 100,
      footer: () => formatCurrency(sumBy(data?.stats.topItems, (x) => x.amount)),
    }),
  ];

  return (
    <ChartSection
      empty={!((data?.stats.topItems.length ?? 0) > 0)}
      loading={fetching}
      error={error}
      divide
      Header={
        <ChartHeader
          title={i18n.t('salesSummary.topItems.topItems')}
          subtitle={[formatTimespan(timespan, i18n), formatTimeRange(times)]
            .filter((x) => x)
            .join('; ')}
          csvOptions={{ data: data?.stats.topItems, columns, filename: 'top-items.csv' }}
        />
      }
    >
      <ViewMoreTable
        data={data?.stats.topItems}
        columns={columns}
        initialState={{ sorting: [{ id: 'quantity', desc: true }] }}
      />
    </ChartSection>
  );
}
